import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { PluginDetailModal } from "./PluginDetailModal";
import { Toaster } from "./ui/sonner";
import { toast } from "sonner";
import { ArrowLeft, Hash, Send, Users, Sparkles, Disc3, ExternalLink } from "lucide-react";
import { ProfileModal } from "./ProfileModal";

const API = process.env.REACT_APP_BACKEND_URL;

const Avatar = ({ username, color, size = "h-8 w-8 text-sm" }) => (
  <span className={`flex ${size} shrink-0 items-center justify-center rounded-full font-heading font-bold text-black`}
        style={{ background: color || "#00F0FF" }}>
    {(username || "?").charAt(0).toUpperCase()}
  </span>
);

const ShareEmbed = ({ shareRef }) => {
  const navigate = useNavigate();
  const isChain = shareRef.mode === "match";
  const openResults = () => {
    sessionStorage.setItem("pg_restore", JSON.stringify({
      query: shareRef.query, mode: shareRef.mode, intent: shareRef.intent,
      analysis: shareRef.analysis, results: shareRef.plugins,
    }));
    navigate("/");
  };
  const names = (shareRef.plugins || []).slice(0, 3).map((p) => p.name).join(" · ");
  return (
    <button
      data-testid="chat-share-embed"
      onClick={openResults}
      className="mt-1 block max-w-md rounded-md border border-cyan-500/25 bg-cyan-500/5 px-4 py-3 text-left transition-colors duration-200 hover:border-cyan-500/50"
    >
      <div className="flex items-center gap-2">
        {isChain ? <Disc3 className="h-3.5 w-3.5 text-cyan-400" /> : <Sparkles className="h-3.5 w-3.5 text-cyan-400" />}
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-400">
          {isChain ? "Shared chain" : "Shared search"}
        </span>
      </div>
      <div className="mt-1.5 font-heading text-sm font-bold text-white">"{shareRef.query}"</div>
      <div className="mt-1 text-xs text-white/50">
        {shareRef.plugins?.length || 0} plugins{names ? ` — ${names}${shareRef.plugins.length > 3 ? "..." : ""}` : ""}
      </div>
      <div className="mt-2 flex items-center gap-1 text-xs font-semibold text-cyan-300">
        Open results <ExternalLink className="h-3 w-3" />
      </div>
    </button>
  );
};

const PluginEmbed = ({ pluginRef, onOpen }) => (
  <button
    data-testid="chat-plugin-embed"
    onClick={() => onOpen(pluginRef.id)}
    className="mt-1 flex items-center gap-3 rounded-md border border-cyan-500/25 bg-cyan-500/5 px-3.5 py-2.5 text-left transition-colors duration-200 hover:border-cyan-500/50"
  >
    <span className="rounded bg-black/50 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-cyan-300">
      {pluginRef.type}
    </span>
    <span className="text-sm font-semibold text-white">{pluginRef.name}</span>
    <span className="text-xs text-white/40">{pluginRef.brand}</span>
    {pluginRef.price != null && (
      <span className="font-mono text-xs text-white/60">{pluginRef.price === 0 ? "FREE" : `$${pluginRef.price}`}</span>
    )}
  </button>
);

export default function CommunityPage() {
  const { user, openAuth } = useAuth();
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState("general");
  const [messages, setMessages] = useState([]);
  const [online, setOnline] = useState([]);
  const [text, setText] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [profileUser, setProfileUser] = useState(null);
  const wsRef = useRef(null);
  const bottomRef = useRef(null);
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    fetch(`${API}/api/chat/channels`).then((r) => r.json()).then(setChannels).catch(() => {});
    fetch(`${API}/api/chat/presence`).then((r) => r.json()).then((d) => setOnline(d.online)).catch(() => {});
  }, []);

  const loadMessages = useCallback((channel) => {
    fetch(`${API}/api/chat/messages/${channel}`).then((r) => r.json()).then(setMessages).catch(() => {});
  }, []);

  useEffect(() => { loadMessages(active); }, [active, loadMessages]);

  useEffect(() => {
    if (!user || user.needs_username) return;
    let ws, closed = false;
    const connect = async () => {
      try {
        const res = await fetch(`${API}/api/auth/ws-ticket`, { credentials: "include" });
        if (!res.ok) return;
        const { ticket } = await res.json();
        ws = new WebSocket(`${API.replace(/^http/, "ws")}/api/ws/chat?ticket=${ticket}`);
        wsRef.current = ws;
        ws.onmessage = (e) => {
          const ev = JSON.parse(e.data);
          if (ev.type === "message" && ev.message.channel_id === activeRef.current) {
            setMessages((m) => (m.some((x) => x.id === ev.message.id) ? m : [...m, ev.message]));
          } else if (ev.type === "presence") {
            setOnline(ev.users);
          }
        };
        ws.onclose = () => { if (!closed) setTimeout(connect, 4000); };
      } catch { /* polling fallback below covers it */ }
    };
    connect();
    return () => { closed = true; ws?.close(); };
  }, [user]);

  useEffect(() => {
    const wsLive = () => wsRef.current && wsRef.current.readyState === WebSocket.OPEN;
    const t = setInterval(() => {
      if (!wsLive()) {
        loadMessages(activeRef.current);
        fetch(`${API}/api/chat/presence`).then((r) => r.json()).then((d) => setOnline(d.online)).catch(() => {});
      }
    }, 5000);
    return () => clearInterval(t);
  }, [loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const body = { channel_id: active, text };
    setText("");
    try {
      const res = await fetch(`${API}/api/chat/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed to send");
      const msg = await res.json();
      setMessages((m) => (m.some((x) => x.id === msg.id) ? m : [...m, msg]));
    } catch (err) {
      toast.error(String(err.message));
    }
  };

  const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="grain flex h-screen flex-col bg-[#09090B] text-white">
      <Toaster theme="dark" position="bottom-right" />
      <header className="z-40 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="flex items-center justify-between px-6 py-3.5">
          <Link data-testid="community-back-link" to="/" className="flex items-center gap-2 text-sm text-white/60 transition-colors duration-200 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> <img src="/logo-mark.png" alt="ZoundEngineer" className="h-6 w-6" />
            <span className="font-heading font-bold">Community</span>
          </Link>
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">
            {online.length} online
          </span>
        </div>
      </header>

      <div className="relative z-10 flex min-h-0 flex-1">
        <aside className="hidden w-52 shrink-0 border-r border-white/10 bg-black/30 p-3 sm:block">
          <div className="px-2 pb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/30">Channels</div>
          {channels.map((ch) => (
            <button
              key={ch.id}
              data-testid={`channel-${ch.id}`}
              onClick={() => setActive(ch.id)}
              className={`flex w-full items-center gap-1.5 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors duration-150 ${
                active === ch.id ? "bg-cyan-500/10 text-cyan-300" : "text-white/50 hover:bg-white/5 hover:text-white"
              }`}
            >
              <Hash className="h-3.5 w-3.5" /> {ch.name}
            </button>
          ))}
        </aside>

        <main className="flex min-w-0 flex-1 flex-col">
          <div className="border-b border-white/10 px-5 py-3">
            <div className="flex items-center gap-2 font-heading text-sm font-bold">
              <Hash className="h-4 w-4 text-cyan-400" /> {active}
            </div>
            <div className="text-xs text-white/35">{channels.find((c) => c.id === active)?.description}</div>
          </div>

          <div data-testid="messages-list" className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
            {messages.length === 0 && (
              <p className="pt-10 text-center font-mono text-xs uppercase tracking-widest text-white/25">
                No messages yet — start the conversation
              </p>
            )}
            {messages.map((m) => (
              <div key={m.id} data-testid="chat-message" className="flex gap-3">
                <Avatar username={m.username} color={m.avatar_color} />
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2">
                    <button
                      data-testid="chat-username-btn"
                      onClick={() => setProfileUser(m.username)}
                      className="text-sm font-bold transition-opacity duration-150 hover:underline hover:opacity-80"
                      style={{ color: m.avatar_color || "#00F0FF" }}
                    >
                      {m.username}
                    </button>
                    <span className="font-mono text-[10px] text-white/30">{fmtTime(m.created_at)}</span>
                  </div>
                  {m.text && <p className="break-words text-sm leading-relaxed text-white/80">{m.text}</p>}
                  {m.plugin_ref && <PluginEmbed pluginRef={m.plugin_ref} onOpen={setSelectedId} />}
                  {m.share_ref && <ShareEmbed shareRef={m.share_ref} />}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-white/10 p-4">
            {user && !user.needs_username ? (
              <form onSubmit={send} className="flex items-center gap-2">
                <input
                  data-testid="chat-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={`Message #${active} — tip: /plugin pro-q 4 shares a plugin`}
                  className="flex-1 rounded-lg border border-white/10 bg-black/40 px-4 py-2.5 text-sm text-white placeholder-white/25 outline-none focus:border-cyan-500/50"
                  maxLength={2000}
                />
                <button data-testid="chat-send-btn" className="rounded-lg bg-cyan-500 p-2.5 text-black transition-colors duration-200 hover:bg-cyan-400">
                  <Send className="h-4 w-4" />
                </button>
              </form>
            ) : (
              <button
                data-testid="chat-join-btn"
                onClick={openAuth}
                className="w-full rounded-lg border border-cyan-500/30 bg-cyan-500/10 py-2.5 text-sm font-semibold text-cyan-300 transition-colors duration-200 hover:bg-cyan-500/20"
              >
                Sign in to join the conversation
              </button>
            )}
          </div>
        </main>

        <aside className="hidden w-48 shrink-0 border-l border-white/10 bg-black/30 p-3 lg:block">
          <div className="flex items-center gap-1.5 px-2 pb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/30">
            <Users className="h-3 w-3" /> Online — {online.length}
          </div>
          <div data-testid="online-list">
            {online.map((u) => (
              <button
                key={u.username}
                data-testid="online-user-btn"
                onClick={() => setProfileUser(u.username)}
                className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left transition-colors duration-150 hover:bg-white/5"
              >
                <span className="relative">
                  <Avatar username={u.username} color={u.color} size="h-6 w-6 text-[10px]" />
                  <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full border border-black bg-emerald-400" />
                </span>
                <span className="truncate text-xs text-white/70">{u.username}</span>
              </button>
            ))}
          </div>
        </aside>
      </div>

      <PluginDetailModal pluginId={selectedId} onClose={() => setSelectedId(null)} />
      <ProfileModal username={profileUser} onClose={() => setProfileUser(null)} />
    </div>
  );
}
